// RoomsSection
import React, { useMemo, useCallback } from "react";
import { ROOM_CATALOG, FLAT_PRESETS } from "./catalogs";
import EstimateRoomsEditor from "./EstimateRoomsEditor";

const uid = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const LETTERS = "АБВГДЕЖЗИКЛМНОПРСТУФХЦЧШЩЭЮЯ";
const wallCode = (roomIndex, wallIndex) => {
    const letter = LETTERS[wallIndex] || String(wallIndex + 1);
    return `${roomIndex + 1}${letter}`;
};

const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
};

const openingM2 = (o) => {
    const qty = Math.max(0, n(o?.qty ?? 1));
    const w = Math.max(0, n(o?.widthMm));
    const h = Math.max(0, n(o?.heightMm));
    return (qty * w * h) / 1e6;
};

const sumOpeningsM2 = (arr) => (Array.isArray(arr) ? arr : []).reduce((a, o) => a + openingM2(o), 0);

const ensureWall = (w, ri, wi, autoLenMm) => ({
    id: w?.id || uid(),
    code: w?.code || wallCode(ri, wi),
    name: w?.name || "",
    lengthMm: w?.lengthMm ?? (autoLenMm ?? ""),
    heightMmOverride: w?.heightMmOverride ?? "",
    doors: Array.isArray(w?.doors) ? w.doors : [],
    windows: Array.isArray(w?.windows) ? w.windows : [],
    sockets: Array.isArray(w?.sockets) ? w.sockets : [],
    switches: Array.isArray(w?.switches) ? w.switches : [],
    notes: w?.notes || "",
});

const ensureRoom = (r, ri) => {
    const hasNew = r && (r.shape || r.rect || r.walls || r.heightMm);

    const floor = r?.floor || "lam";
    const warm = !!r?.warm;
    const heightMm = n(r?.heightMm ?? 2700);

    const ceilingType = (r?.ceilingType ?? "") === "" ? "" : r.ceilingType;
    const ceilingDropMm =
        r?.ceilingDropMm === "" || r?.ceilingDropMm === null || r?.ceilingDropMm === undefined
            ? 0
            : n(r.ceilingDropMm);

    const ceilingAreaM2Override =
        r?.ceilingAreaM2Override === undefined || r?.ceilingAreaM2Override === null
            ? ""
            : r.ceilingAreaM2Override;

    const ceilingLights = Array.isArray(r?.ceilingLights) ? r.ceilingLights : [];

    if (!hasNew) {
        const lenM = n(r?.len);
        const widM = n(r?.wid);
        const Lmm = lenM > 0 ? Math.round(lenM * 1000) : "";
        const Wmm = widM > 0 ? Math.round(widM * 1000) : "";

        const L = n(Lmm);
        const W = n(Wmm);

        const walls = [0, 1, 2, 3].map((wi) => {
            const isL = wi === 0 || wi === 2;
            const autoLen = isL ? L : W;
            return ensureWall({}, ri, wi, autoLen);
        });

        return {
            id: r?.id || uid(),
            name: r?.name || `Комната ${ri + 1}`,
            shape: "rectangle",
            heightMm,
            floor,
            warm,
            rect: { lengthMm: Lmm, widthMm: Wmm },
            floorAreaM2Override: "",
            ceilingType,
            ceilingDropMm,
            ceilingAreaM2Override,
            ceilingLights,
            walls,
            notes: r?.notes || "",
            len: r?.len ?? "",
            wid: r?.wid ?? "",
            area: r?.area ?? 0,
            wall: r?.wall ?? 0,
        };
    }

    const shape = r?.shape === "polygon" ? "polygon" : "rectangle";
    const rect = r?.rect || {};
    const Lmm = rect?.lengthMm ?? "";
    const Wmm = rect?.widthMm ?? "";

    let walls = Array.isArray(r?.walls) ? r.walls : [];

    if (shape === "rectangle") {
        walls = walls.slice(0, 4);
        while (walls.length < 4) walls.push({});

        const L = n(Lmm);
        const W = n(Wmm);

        walls = walls.map((w, wi) => {
            const isL = wi === 0 || wi === 2;
            const autoLen = isL ? L : W;
            return ensureWall(w, ri, wi, autoLen);
        });
    } else {
        walls = walls.map((w, wi) => ensureWall(w, ri, wi));
    }

    return {
        id: r?.id || uid(),
        name: r?.name || `Комната ${ri + 1}`,
        shape,
        heightMm,
        floor,
        warm,
        rect: { lengthMm: Lmm, widthMm: Wmm },
        floorAreaM2Override: r?.floorAreaM2Override ?? "",
        ceilingType,
        ceilingDropMm,
        ceilingAreaM2Override,
        ceilingLights,
        walls,
        notes: r?.notes || "",
        len: r?.len ?? "",
        wid: r?.wid ?? "",
        area: r?.area ?? 0,
        wall: r?.wall ?? 0,
    };
};

const decorateLegacyRooms = (rooms) => {
    const arr = Array.isArray(rooms) ? rooms : [];

    return arr.map((room) => {
        const shape = room?.shape === "polygon" ? "polygon" : "rectangle";
        const heightMm = Math.max(0, n(room?.heightMm ?? 2700));

        let floorM2 = 0;
        let lenM = room?.len;
        let widM = room?.wid;

        if (shape === "rectangle") {
            const Lmm = n(room?.rect?.lengthMm);
            const Wmm = n(room?.rect?.widthMm);
            floorM2 = (Math.max(0, Lmm) * Math.max(0, Wmm)) / 1e6;
            lenM = Lmm > 0 ? +(Lmm / 1000).toFixed(2) : room?.len;
            widM = Wmm > 0 ? +(Wmm / 1000).toFixed(2) : room?.wid;
        } else {
            const ov = n(room?.floorAreaM2Override);
            floorM2 = ov > 0 ? ov : n(room?.area);
        }

        const walls = Array.isArray(room?.walls) ? room.walls : [];
        let wallsGross = 0;
        let openings = 0;

        walls.forEach((w) => {
            const wallH = Math.max(0, n(w?.heightMmOverride) || heightMm);
            const wallL = Math.max(0, n(w?.lengthMm));
            wallsGross += (wallL * wallH) / 1e6;

            openings += sumOpeningsM2(w?.doors);
            openings += sumOpeningsM2(w?.windows);
        });

        const wallsNet = Math.max(0, wallsGross - openings);

        return {
            ...room,
            heightMm,
            len: lenM,
            wid: widM,
            area: +floorM2.toFixed(2),
            wall: +wallsNet.toFixed(2),
        };
    });
};

const makeRoomFromCatalog = (it, index) => {
    const base = it || {};
    const Lmm = base.len ? Math.round(n(base.len) * 1000) : "";
    const Wmm = base.wid ? Math.round(n(base.wid) * 1000) : "";

    const L = n(Lmm);
    const W = n(Wmm);

    return {
        id: uid(),
        name: base.name || `Комната ${index + 1}`,
        shape: "rectangle",
        heightMm: 2700,
        floor: base.floor || "lam",
        warm: !!base.warm,
        rect: { lengthMm: Lmm, widthMm: Wmm },
        floorAreaM2Override: "",
        ceilingType: "",
        ceilingDropMm: 0,
        ceilingAreaM2Override: "",
        ceilingLights: [],
        walls: [0, 1, 2, 3].map((wi) => {
            const isL = wi === 0 || wi === 2;
            const autoLen = isL ? L : W;
            return ensureWall({}, index, wi, autoLen);
        }),
        notes: "",
        len: "",
        wid: "",
        area: 0,
        wall: 0,
    };
};

export default function RoomsSection({ data, clearRooms, setRooms, setParams }) {
    const roomsForEditor = useMemo(() => {
        const src = Array.isArray(data?.rooms) ? data.rooms : [];
        return src.map((r, ri) => ensureRoom(r, ri));
    }, [data?.rooms]);

    const commitRooms = useCallback(
        (nextRooms) => {
            const normalized = (Array.isArray(nextRooms) ? nextRooms : []).map((r, ri) => ensureRoom(r, ri));
            const decorated = decorateLegacyRooms(normalized);
            setRooms(decorated);
        },
        [setRooms]
    );

    const onClear = useCallback(() => {
        if (typeof clearRooms === "function") clearRooms();
        else setRooms([]);
    }, [clearRooms, setRooms]);

    const addEmptyRoomTop = useCallback(() => {
        const cur = roomsForEditor || [];
        const r = makeRoomFromCatalog(null, 0);
        commitRooms([r, ...cur]);
    }, [roomsForEditor, commitRooms]);

    const addFromCatalogTop = useCallback(() => {
        const key = document.getElementById("selRoomCatalog")?.value;
        const it = ROOM_CATALOG.find((x) => x.key === key);
        const cur = roomsForEditor || [];
        const r = makeRoomFromCatalog(it, 0);
        commitRooms([r, ...cur]);
    }, [roomsForEditor, commitRooms]);

    const addPreset = useCallback(
        (kind) => {
            const map = new Map(ROOM_CATALOG.map((r) => [r.key, r]));
            const keys = FLAT_PRESETS[kind] || [];
            const arr = keys.map((k, i) => makeRoomFromCatalog(map.get(k), i));
            commitRooms(arr);
        },
        [commitRooms]
    );

    return (
        <div className="card">
            <div className="section-title"><i></i>Помещения и параметры</div>

            <div className="actions" style={{ justifyContent: "space-between", marginTop: 0 }}>
                <div className="row cols-3">
                    <div>
                        <label>Пресет квартиры</label>
                        <select onChange={(e) => e.target.value && addPreset(e.target.value)}>
                            <option value="">— выбрать —</option>
                            {Object.keys(FLAT_PRESETS).map((k) => (
                                <option key={k} value={k}>{k}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label>Каталог комнат</label>
                        <select id="selRoomCatalog">
                            {ROOM_CATALOG
                                .filter((x) => !(data.rooms || []).some((r) => r.name === x.name))
                                .sort((a, b) => a.name.localeCompare(b.name, "ru"))
                                .map((it) => (
                                    <option key={it.key} value={it.key}>{it.name}</option>
                                ))}
                        </select>
                    </div>

                    <div className="actions">
                        <button className="ghost" onClick={addFromCatalogTop}>
                            Добавить из списка
                        </button>
                    </div>
                </div>

                <div>
                    <button className="danger" onClick={onClear}>Очистить помещения</button>
                    <button className="ghost" onClick={addEmptyRoomTop}>+ Помещение</button>
                </div>
            </div>

            <EstimateRoomsEditor value={roomsForEditor} onChange={commitRooms} />

            <div className="row cols-4" style={{ marginTop: 8 }}>
                <div>
                    <label>Толщина стяжки, мм</label>
                    <input
                        value={data.params?.screed_mm ?? 60}
                        onChange={(e) =>
                            setParams({
                                ...data.params,
                                screed_mm: Number(e.target.value) || 0,
                            })
                        }
                    />
                </div>

                <div>
                    <label>Плинтус, пог.м</label>
                    <input
                        value={data.params?.plinth_m ?? 42}
                        onChange={(e) =>
                            setParams({
                                ...data.params,
                                plinth_m: Number(e.target.value) || 0,
                            })
                        }
                    />
                </div>

                <div>
                    <label>Межкомнатные двери, шт</label>
                    <input
                        value={data.params?.doors ?? 5}
                        onChange={(e) =>
                            setParams({
                                ...data.params,
                                doors: Number(e.target.value) || 0,
                            })
                        }
                    />
                </div>

                <div>
                    <label>Электрика override (точки)</label>
                    <input
                        value={data.params?.elec_override ?? ""}
                        onChange={(e) =>
                            setParams({
                                ...data.params,
                                elec_override: e.target.value === "" ? null : Number(e.target.value) || 0,
                            })
                        }
                        placeholder="null = авто"
                    />
                </div>
            </div>
        </div>
    );
}
